import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock,
  Minus,
  Play,
  BookOpen,
  AlertTriangle,
  Languages,
  Calculator,
  Monitor,
  Heart,
  Atom,
  FlaskConical,
  Dna,
  Sigma,
  Landmark,
  Globe,
  Scale,
  Microscope,
  FileText,
  Banknote,
  Briefcase,
  Sprout,
  Home as HomeIcon,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';

import { useShuddhoshikhaContext } from '../../hooks/useShuddhoshikhaContext';
import { useMockTest } from '../../hooks/useMockTest';
import { ShuddhoshikhaBreadcrumb, type BreadcrumbSegment } from './ShuddhoshikhaBreadcrumb';
import MockTestEngine from './MockTestEngine';

interface MockTestsProps {
  onBack: () => void;
  isDarkMode?: boolean;
}

const iconMap: Record<string, React.ElementType> = {
  BookOpen: BookOpen,
  Languages: Languages,
  Calculator: Calculator,
  Monitor: Monitor,
  Heart: Heart,
  Atom: Atom,
  FlaskConical: FlaskConical,
  Dna: Dna,
  Sigma: Sigma,
  Landmark: Landmark,
  Globe: Globe,
  Scale: Scale,
  Microscope: Microscope,
  FileText: FileText,
  Banknote: Banknote,
  Briefcase: Briefcase,
  Sprout: Sprout,
  Home: HomeIcon,
};

const getSubjectGradient = (subjectId: string): string => {
  const map: Record<string, string> = {
    sub_bn1: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_bn2: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_eng1: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_eng2: 'from-indigo-400 to-purple-500 shadow-purple-500/15',
    sub_math: 'from-emerald-400 to-teal-500 shadow-teal-500/15',
    sub_ict: 'from-slate-400 to-slate-600 shadow-slate-500/15',
    sub_religion: 'from-rose-400 to-pink-500 shadow-rose-500/15',
    sub_phy: 'from-blue-500 to-indigo-600 shadow-indigo-500/15',
    sub_chem: 'from-cyan-400 to-teal-500 shadow-teal-500/15',
    sub_bio: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    sub_hmath: 'from-violet-500 to-purple-600 shadow-purple-500/15',
    sub_hist: 'from-amber-500 to-orange-600 shadow-orange-500/15',
    sub_geo: 'from-cyan-400 to-blue-500 shadow-blue-500/15',
    sub_civ: 'from-purple-400 to-indigo-500 shadow-indigo-500/15',
    sub_gsci: 'from-sky-400 to-blue-500 shadow-blue-500/15',
    sub_acc: 'from-teal-400 to-emerald-500 shadow-emerald-500/15',
    sub_fin: 'from-amber-400 to-yellow-500 shadow-amber-500/15',
    sub_bus: 'from-blue-400 to-indigo-500 shadow-indigo-500/15',
    opt_agri: 'from-green-400 to-emerald-500 shadow-emerald-500/15',
    opt_home: 'from-rose-400 to-pink-500 shadow-pink-500/15',
  };
  return map[subjectId] || 'from-sky-400 to-blue-500 shadow-blue-500/15';
};

const examTypes = [
  { id: 'mcq', label: 'MCQ', desc: 'বহুনির্বাচনি প্রশ্ন' },
  { id: 'cq', label: 'CQ', desc: 'সৃজনশীল প্রশ্ন' },
  { id: 'both', label: 'উভয়', desc: 'MCQ + CQ' },
];


const toBengaliNumber = (num: number | string): string => {
  const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().split('').map(digit => {
    const parsed = parseInt(digit);
    return isNaN(parsed) ? digit : bengaliDigits[parsed];
  }).join('');
};

const getFormatLabelInBengali = (format: string): string => {
  if (format === 'mcq') return 'এমসিকিউ';
  if (format === 'cq') return 'সৃজনশীল';
  if (format === 'both') return 'এমসিকিউ + সৃজনশীল';
  return format.toUpperCase();
};

export default function MockTests({ onBack, isDarkMode }: MockTestsProps) {
  const { step, setStep, config, setConfig } = useMockTest();
  const [expandedSubjects, setExpandedSubjects] = useState<string[]>([]);
  const { profile, subjects: activeSubjects, isLoading: contextLoading } = useShuddhoshikhaContext();
  const [isEngineActive, setIsEngineActive] = useState<boolean>(false);

  const userOptional = profile?.sh_optional || null;

  // Verify if a subject is in the user's active group curriculum
  const isCurriculumSubject = (subjectId: string): boolean => {
    return activeSubjects.some((s) => s.id === subjectId);
  };

  // Sync state changes to browser history
  useEffect(() => {
    const current = window.history.state;
    if (current && current.view === 'চর্চাঘর' && current.subView === 'mockTests') {
      if (current.step !== step) {
        window.history.pushState({
          view: 'চর্চাঘর',
          subView: 'mockTests',
          step,
          config,
        }, '');
      }
    }
  }, [step, config]);

  // Listen to popstate event (browser back button)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state && state.view === 'চর্চাঘর' && state.subView === 'mockTests') {
        if (state.step) setStep(state.step);
        if (state.config) {
          setConfig(state.config);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const toggleExpandSubject = (subjectId: string) => {
    setExpandedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((id) => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const toggleAllChapters = (subjectId: string, chapters: { id: string }[]) => {
    const currentSelected = config.chapters[subjectId] || [];
    const allChapterIds = chapters.map((c) => c.id);
    const isAllSelected = allChapterIds.every((id) => currentSelected.includes(id));

    setConfig((prev) => ({
      ...prev,
      chapters: {
        ...prev.chapters,
        [subjectId]: isAllSelected ? [] : allChapterIds,
      }
    }));
  };

  const toggleSubject = (subjectId: string) => {
    if (!isCurriculumSubject(subjectId)) {
      return; // Block subjects out of current track
    }

    const isSelected = config.subjects.includes(subjectId);
    const updatedSubjects = isSelected
      ? config.subjects.filter((id) => id !== subjectId)
      : [...config.subjects, subjectId];

    // Initialize defaults if newly selected
    const updatedTotals = { ...config.totals };
    if (!isSelected) {
      updatedTotals[subjectId] = 10;
    } else {
      delete updatedTotals[subjectId];
    }

    setConfig((prev) => ({
      ...prev,
      subjects: updatedSubjects,
      totals: updatedTotals,
    }));
  };

  const toggleChapter = (subjectId: string, chapterId: string) => {
    const current = config.chapters[subjectId] || [];
    const updated = current.includes(chapterId)
      ? current.filter((id) => id !== chapterId)
      : [...current, chapterId];

    setConfig((prev) => ({
      ...prev,
      chapters: {
        ...prev.chapters,
        [subjectId]: updated,
      }
    }));
  };

  const updateQuestionCount = (subjectId: string, count: number) => {
    const val = Math.max(1, Math.min(count, 100));
    const updatedTotals = { ...config.totals, [subjectId]: val };
    const totalQuestions = Object.values(updatedTotals).reduce((a, b) => a + b, 0);

    setConfig((prev) => ({
      ...prev,
      totals: updatedTotals,
      duration: totalQuestions // calculated 1 min / MCQ default
    }));
  };

  const getSelectedSubjectsData = () => {
    return activeSubjects.filter((s) => config.subjects.includes(s.id));
  };

  const totalQuestions = Object.values(config.totals).reduce((a, b) => a + b, 0);

  const handleLaunch = () => {
    setIsEngineActive(true);
  };

  const getBreadcrumbSegments = (): BreadcrumbSegment[] => {
    const stepLabels = ['বিষয় ও ধরণ', 'অধ্যায়', 'প্রশ্ন সংখ্যা', 'পরীক্ষা শুরু'];
    const segments: BreadcrumbSegment[] = [
      { label: 'মক পরীক্ষা', target: 'step1' }
    ];
    if (step > 1) {
      segments.push({ label: `ধাপ ${toBengaliNumber(step)}: ${stepLabels[step - 1]}`, target: `step${step}` });
    }
    return segments;
  };

  const handleNavigate = (target: string) => {
    if (target === 'home') {
      onBack();
    } else if (target === 'step1') {
      setStep(1);
    } else if (target === 'step2' && config.subjects.length > 0) {
      setStep(2);
    } else if (target === 'step3' && step > 2) {
      setStep(3);
    }
  };

  if (isEngineActive) {
    return (
      <MockTestEngine
        config={config}
        onBack={() => setIsEngineActive(false)}
        subjectsData={activeSubjects}
        isDarkMode={isDarkMode}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Dynamic Breadcrumbs */}
      <ShuddhoshikhaBreadcrumb 
        segments={getBreadcrumbSegments()} 
        onNavigate={handleNavigate} 
      />

      {contextLoading ? (
        <div className="space-y-4">
          <div>
            <div className="h-6 w-48 skel-shimmer rounded-lg mb-2" />
            <div className="h-4 w-72 skel-shimmer rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E293B]/60 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl skel-shimmer flex-shrink-0" />
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="h-4 w-2/3 skel-shimmer rounded-md" />
                    <div className="h-3 w-1/3 skel-shimmer rounded-md" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Stepper */}
          <div className="flex items-center justify-between w-full px-1 relative pb-0 lg:pb-8">
            {/* Horizontal connecting background line */}
            <div className="absolute left-6 right-6 top-4 h-0.5 bg-gray-200 dark:bg-white/10 -z-0" />

            {[1, 2, 3, 4].map((s) => {
              const stepLabels = ['বিষয় ও ধরণ', 'অধ্যায়', 'প্রশ্ন সংখ্যা', 'পরীক্ষা শুরু'];
              return (
                <div key={s} className="flex flex-col items-center relative z-10 flex-1">
                  {/* Circle */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bangla text-xs font-bold transition-colors ${
                      step >= s ? 'bg-[#00CBA9] text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-400 border border-gray-200 dark:border-slate-700'
                    }`}
                  >
                    {step > s ? <Check className="w-4 h-4" /> : toBengaliNumber(s)}
                  </div>
                  {/* Label (centered below circle on desktop) */}
                  <span
                    className={`font-bangla text-xs mt-1.5 hidden lg:block whitespace-nowrap absolute top-9 left-1/2 -translate-x-1/2 text-center ${
                      step >= s ? 'text-[#1F1F1F] dark:text-[#E8EAED] font-semibold' : 'text-gray-400'
                    }`}
                  >
                    {stepLabels[s - 1]}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step 1: Subject & Type */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-5"
            >
              <div>
                <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] mb-1">বিষয় নির্বাচন করুন</h4>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">এক বা একাধিক বিষয় বেছে নিন</p>
              </div>

              {!userOptional && (
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="font-bangla text-sm text-amber-700">
                    আপনার অপশনাল সাবজেক্ট সিলেক্ট করুন প্রোফাইল থেকে
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeSubjects.map((subject) => {
                  const Icon = iconMap[subject.icon] || BookOpen;
                  const isSelected = config.subjects.includes(subject.id);

                  return (
                    <button
                      key={subject.id}
                      onClick={() => toggleSubject(subject.id)}
                      className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center justify-between group relative ${
                        isSelected
                          ? 'border-[#00CBA9] bg-white dark:bg-white/5 shadow-[0_8px_24px_rgba(0,203,169,0.05)]'
                          : 'border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md hover:border-[#00CBA9]/30 hover:shadow-[0_4px_16px_rgba(0,0,0,0.02)]'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${getSubjectGradient(subject.id)} flex items-center justify-center text-white shadow-md border border-white/10 relative overflow-hidden flex-shrink-0`}>
                          <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED] group-hover:text-[#00CBA9] transition-colors truncate">
                            {subject.name}
                          </h5>
                          <p className="font-bangla text-xs text-gray-400 mt-0.5">
                            {toBengaliNumber(subject.chapters.length)}টি অধ্যায়
                          </p>
                        </div>
                      </div>
                      {isSelected ? (
                        <Check className="w-5 h-5 text-[#00CBA9] ml-2 flex-shrink-0" />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div>
                <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] mb-3">পরীক্ষার ধরণ</h4>
                <div className="grid grid-cols-3 gap-3">
                  {examTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setConfig(prev => ({ ...prev, formats: [type.id] }))}
                      className={`p-4 rounded-2xl border-2 transition-all text-center cursor-pointer ${
                        config.formats.includes(type.id)
                          ? 'border-[#00CBA9] bg-[#00CBA9]/5 text-[#00CBA9]'
                          : 'border-gray-100 dark:border-white/5 bg-white dark:bg-[#1E1E1E]/60 hover:border-[#00CBA9]/30'
                      }`}
                    >
                      <p className="font-english text-sm font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{type.label}</p>
                      <p className="font-bangla text-xs text-gray-500 mt-1">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-bangla text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4" />
                  পেছনে
                </button>
                <button
                  onClick={() => {
                    if (config.subjects.length > 0) {
                      setStep(2);
                      setExpandedSubjects([config.subjects[0]]);
                    }
                  }}
                  disabled={config.subjects.length === 0}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  পরবর্তী
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Chapter Selection Accordion */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div>
                <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] mb-1">অধ্যায় নির্বাচন করুন</h4>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">প্রতিটি বিষয়ের জন্য অধ্যায় বেছে নিন</p>
              </div>

              <div className="space-y-3">
                {getSelectedSubjectsData().map((subject) => {
                  const isExpanded = expandedSubjects.includes(subject.id);
                  const allChapterIds = subject.chapters.map((c) => c.id);
                  const selectedCount = (config.chapters[subject.id] || []).length;
                  const isAllSelected = selectedCount === allChapterIds.length;
                  const isSomeSelected = selectedCount > 0 && selectedCount < allChapterIds.length;

                  return (
                    <div key={subject.id} className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md">
                      {/* Header Row */}
                      <div className="flex items-center justify-between p-4 transition-colors select-none border-b border-gray-100/50 dark:border-white/5">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Subject Checkbox */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAllChapters(subject.id, subject.chapters);
                            }}
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer ${
                              isAllSelected ? 'border-[#00CBA9] bg-[#00CBA9]' : isSomeSelected ? 'border-[#00CBA9] bg-[#00CBA9]/10' : 'border-gray-300 bg-white dark:bg-white/5'
                            }`}
                          >
                            {isAllSelected ? (
                              <Check className="w-3 h-3 text-white stroke-[3px]" />
                            ) : isSomeSelected ? (
                              <div className="w-2 h-0.5 bg-[#00CBA9] rounded-sm" />
                            ) : null}
                          </button>
                          
                          {/* Subject Name */}
                          <div
                            onClick={() => toggleExpandSubject(subject.id)}
                            className="flex-1 cursor-pointer py-1 text-left"
                          >
                            <span className="font-bangla text-sm sm:text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">
                              {subject.name}
                            </span>
                          </div>
                        </div>
                        
                        {/* Chevron */}
                        <button
                          onClick={() => toggleExpandSubject(subject.id)}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-50 dark:hover:bg-white/5 transition-colors cursor-pointer"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </div>

                      {/* Chapters List */}
                      {isExpanded && (
                        <div className="bg-white dark:bg-transparent divide-y divide-gray-50 dark:divide-white/5">
                          {subject.chapters.map((chapter) => {
                            const isChapterSelected = (config.chapters[subject.id] || []).includes(chapter.id);
                            return (
                              <div
                                key={chapter.id}
                                onClick={() => toggleChapter(subject.id, chapter.id)}
                                className="flex items-center gap-4 px-8 py-3.5 hover:bg-[#F8FAFC] dark:hover:bg-white/5 cursor-pointer transition-colors text-left"
                              >
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                                  isChapterSelected ? 'border-[#00CBA9] bg-[#00CBA9]' : 'border-gray-300 bg-white dark:bg-white/5'
                                }`}>
                                  {isChapterSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />}
                                </div>
                                <span className="font-bangla text-sm text-[#1F1F1F] dark:text-[#E8EAED] font-normal select-none">
                                  {chapter.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-bangla text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4" />
                  পেছনে
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors cursor-pointer"
                >
                  পরবর্তী
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Question Configuration */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-5"
            >
              <div>
                <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] mb-1">প্রশ্ন সংখ্যা নির্ধারণ</h4>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">প্রতি বিষয়ের জন্য প্রশ্ন সংখ্যা সেট করুন</p>
              </div>

              <div className="space-y-4">
                {getSelectedSubjectsData().map((subject) => {
                  const limit = subject.chapters.reduce((a, c) => a + c.totalQuestions, 0);
                  return (
                    <div key={subject.id} className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-2xl p-5 border border-gray-100 dark:border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED]">{subject.name}</h5>
                        <span className="font-bangla text-xs text-gray-400">
                          উপলব্ধ: {toBengaliNumber(limit)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max={Math.min(50, limit)}
                          value={config.totals[subject.id] || 10}
                          onChange={(e) => updateQuestionCount(subject.id, parseInt(e.target.value))}
                          className="flex-1 h-2 bg-gray-100 dark:bg-white/5 rounded-full appearance-none cursor-pointer accent-[#00CBA9]"
                        />
                        <div className="w-16">
                          <input
                            type="number"
                            min="1"
                            max={limit}
                            value={config.totals[subject.id] || 10}
                            onChange={(e) => updateQuestionCount(subject.id, parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/5 dark:bg-white/5 font-english text-sm text-center focus:outline-none focus:border-[#00CBA9] text-[#1F1F1F] dark:text-[#E8EAED]"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Global Settings */}
              <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-3xl p-6 border border-gray-100 dark:border-white/5 space-y-4 shadow-sm">
                <h5 className="font-bangla text-lg font-bold text-[#1F1F1F] dark:text-[#E8EAED]">গ্লোবাল সেটিংস</h5>

                {/* Negative Marking Row */}
                <div className="flex items-center justify-between p-4 bg-[#F8FAFC] dark:bg-white/5 rounded-2xl border border-gray-50/50 dark:border-white/5">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-bangla text-sm sm:text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] leading-tight">নেগেটিভ মার্কিং</p>
                      <p className="font-bangla text-xs text-gray-400 mt-1">প্রতি ভুল উত্তরে -০.২৫ কাটা যাবে</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfig((prev) => ({ ...prev, negativeMarking: !prev.negativeMarking }))}
                    className={`w-12 h-6.5 rounded-full p-0.5 transition-colors duration-200 ease-in-out cursor-pointer relative ${
                      config.negativeMarking ? 'bg-[#00CBA9]' : 'bg-gray-200 dark:bg-white/10'
                    }`}
                  >
                    <div
                      className={`w-5.5 h-5.5 rounded-full bg-white shadow-md transform transition-transform duration-200 ease-in-out ${
                        config.negativeMarking ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Total Time Row */}
                <div className="flex items-center justify-between p-4 bg-[#F8FAFC] dark:bg-white/5 rounded-2xl border border-gray-50/50 dark:border-white/5">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-bangla text-sm sm:text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] leading-tight">মোট সময়</p>
                      <p className="font-bangla text-xs text-gray-400 mt-1">প্রতি MCQ-তে ১ মিনিট হিসাবে</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 select-none">
                    <button
                      onClick={() => setConfig((prev) => ({ ...prev, duration: Math.max(5, prev.duration - 5) }))}
                      className="w-8 h-8 rounded-xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/5 flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-white/10 active:scale-95 transition-all text-gray-600 dark:text-gray-300 font-bold cursor-pointer"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] w-12 text-center">{toBengaliNumber(config.duration)} মি</span>
                    <button
                      onClick={() => setConfig((prev) => ({ ...prev, duration: Math.min(180, prev.duration + 5) }))}
                      className="w-8 h-8 rounded-xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/5 flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-white/10 active:scale-95 transition-all text-gray-600 dark:text-gray-300 font-bold cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-bangla text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4" />
                  পেছনে
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors cursor-pointer"
                >
                  পরবর্তী
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Review & Launch Summary Table */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-5"
            >
              <div>
                <h4 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED] mb-1">পরীক্ষার সারসংক্ষেপ</h4>
                <p className="font-bangla text-sm text-gray-500 dark:text-[#A0A5AD]">সকল তথ্য যাচাই করে পরীক্ষা শুরু করুন</p>
              </div>

              <div className="bg-white dark:bg-[#1E1E1E]/60 dark:backdrop-blur-md rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-[#00CBA9]/5 to-[#00A3FF]/5 border-b border-gray-50 dark:border-white/5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00CBA9]/10 flex items-center justify-center">
                      <Play className="w-6 h-6 text-[#00CBA9]" />
                    </div>
                    <div className="text-left">
                      <h5 className="font-bangla text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">মক পরীক্ষা</h5>
                      <p className="font-bangla text-xs text-gray-500 uppercase">{config.formats.map(getFormatLabelInBengali).join(' + ')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-white/5">
                      <p className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{toBengaliNumber(totalQuestions)}</p>
                      <p className="font-bangla text-xs text-gray-500 mt-1">মোট প্রশ্ন</p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-white/5">
                      <p className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{toBengaliNumber(config.duration)}</p>
                      <p className="font-bangla text-xs text-gray-500 mt-1">মোট সময় (মি)</p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-white/5">
                      <p className="font-bangla text-2xl font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{toBengaliNumber(config.subjects.length)}</p>
                      <p className="font-bangla text-xs text-gray-500 mt-1">বিষয়</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 dark:border-white/5 pt-4">
                    <h6 className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED] mb-3 text-left">বিষয়ভিত্তিক বণ্টন</h6>
                    <div className="space-y-2">
                      {getSelectedSubjectsData().map((subject) => (
                        <div key={subject.id} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-white/5 last:border-0">
                          <span className="font-bangla text-sm text-gray-600 dark:text-gray-300">{subject.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="font-bangla text-sm text-gray-400">
                              {toBengaliNumber((config.chapters[subject.id] || []).length)}টি অধ্যায়
                            </span>
                            <span className="font-bangla text-sm font-semibold text-[#1F1F1F] dark:text-[#E8EAED]">
                              {toBengaliNumber(config.totals[subject.id] || 10)}টি প্রশ্ন
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {config.negativeMarking && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <p className="font-bangla text-xs text-red-600 dark:text-red-400">নেগেটিভ মার্কিং সক্রিয়: প্রতি ভুলে -০.২৫</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-bangla text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4" />
                  পেছনে
                </button>
                <button
                  onClick={handleLaunch}
                  className="flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-[#00CBA9] text-white font-bangla text-sm font-medium hover:bg-[#00b898] transition-colors shadow-lg hover:shadow-xl cursor-pointer"
                >
                  <Play className="w-4 h-4" />
                  পরীক্ষা শুরু করুন
                </button>
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
